
/**
 * SEO Component
 * 
 * A simple component that handles basic SEO metadata using document head manipulation.
 * It automatically matches the current route with appropriate SEO data and allows
 * for component-level overrides.
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SEOData, getSEOForPath } from '@/config/seo.config';

interface SEOProps {
  overrideSEO?: Partial<SEOData> & {
    image?: string;
    url?: string;
  };
}

const SEO: React.FC<SEOProps> = ({ overrideSEO = {} }) => {
  const location = useLocation();
  const path = location.pathname;
  
  // Get base SEO data for the current path
  const baseSEO = getSEOForPath(path);
  
  // Merge with any overrides
  const seo: SEOData = {
    ...baseSEO,
    ...overrideSEO,
  };

  // Construct the full title
  const fullTitle = seo.title.includes('AI Wealth Manager') 
    ? seo.title 
    : `${seo.title} | AI Wealth Manager`;

  const updateMetaTag = (name: string, content: string, property = false) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector(selector);
    if (meta) {
      meta.setAttribute('content', content);
    } else {
      meta = document.createElement('meta');
      if (property) {
        meta.setAttribute('property', name);
      } else {
        meta.setAttribute('name', name);
      }
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };

  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update meta tags
    updateMetaTag('description', seo.description);
    updateMetaTag('keywords', seo.keywords.join(', '));
    
    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', seo.description, true);
    updateMetaTag('og:url', window.location.href, true);
    updateMetaTag('og:type', 'article', true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', seo.description);
    
    // Add image if provided
    if (overrideSEO.image) {
      updateMetaTag('og:image', overrideSEO.image, true);
      updateMetaTag('twitter:image', overrideSEO.image);
    }
  }, [fullTitle, seo.description, seo.keywords, overrideSEO.image]);

  return null; // This component doesn't render anything visible
};

export default SEO;
